import { useState, useEffect } from "react";
import { 
  ArrowRightLeft, 
  Activity, 
  AlertTriangle, 
  Scale,
  Clock,
  Server,
  RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api from "../services/api";
import { toast } from "sonner";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [latestWeighing, setLatestWeighing] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, weighingRes, logsRes] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/weighings/latest"),
        api.get("/logs?limit=5")
      ]);
      setStats(statsRes.data);
      setLatestWeighing(weighingRes.data);
      setRecentLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    fetchData();
    toast.success("Dashboard atualizado");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("pt-BR");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "connected": return "connected";
      case "configured": return "configured";
      default: return "disconnected";
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono">Overview</h1>
          <p className="text-sm text-zinc-500">Monitoramento em tempo real do gateway</p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          className="gap-2"
          data-testid="refresh-dashboard-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800 card-hover" data-testid="stat-flows">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Fluxos Ativos</CardTitle>
            <ArrowRightLeft className="w-4 h-4 text-cyan-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white font-mono">
              {stats?.active_flows || 0}
              <span className="text-lg text-zinc-500">/{stats?.total_flows || 0}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 card-hover" data-testid="stat-weighings">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Pesagens Hoje</CardTitle>
            <Scale className="w-4 h-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white font-mono">
              {stats?.total_weighings_today || 0}
            </div>
            {stats?.new_weighings > 0 && (
              <p className="text-xs text-cyan-400 mt-1">
                {stats.new_weighings} novas
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 card-hover" data-testid="stat-logs">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Logs Hoje</CardTitle>
            <Activity className="w-4 h-4 text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white font-mono">
              {stats?.total_logs_today || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800 card-hover" data-testid="stat-errors">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">Erros Hoje</CardTitle>
            <AlertTriangle className="w-4 h-4 text-red-400" />
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold font-mono ${stats?.errors_today > 0 ? 'text-red-400' : 'text-white'}`}>
              {stats?.errors_today || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Connection Status & Latest Weighing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Connection Status */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="connection-status-card">
          <CardHeader>
            <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
              <Server className="w-5 h-5 text-cyan-400" />
              Status das Conexões
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
              <div className="flex items-center gap-3">
                <span className={`connection-dot ${getStatusColor(stats?.rodopeso_status)}`} />
                <div>
                  <p className="text-sm font-medium text-white">RODOPESO</p>
                  <p className="text-xs text-zinc-500">REST API</p>
                </div>
              </div>
              <Badge className="badge-rest">{stats?.rodopeso_status || "disconnected"}</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-sm">
              <div className="flex items-center gap-3">
                <span className={`connection-dot ${getStatusColor(stats?.senior_status)}`} />
                <div>
                  <p className="text-sm font-medium text-white">SENIOR ERP</p>
                  <p className="text-xs text-zinc-500">SOAP WebService</p>
                </div>
              </div>
              <Badge className="badge-soap">{stats?.senior_status || "disconnected"}</Badge>
            </div>
            {stats?.last_sync && (
              <div className="flex items-center gap-2 text-xs text-zinc-500 mt-2">
                <Clock className="w-3 h-3" />
                Última sincronização: {formatDate(stats.last_sync)}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Latest Weighing */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="latest-weighing-card">
          <CardHeader>
            <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
              <Scale className="w-5 h-5 text-green-400" />
              Última Pesagem
            </CardTitle>
          </CardHeader>
          <CardContent>
            {latestWeighing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-zinc-500">Placa</p>
                    <p className="text-lg font-mono font-bold text-white">{latestWeighing.placa || "-"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Peso Bruto</p>
                    <p className="text-lg font-mono font-bold text-cyan-400">{Math.round(latestWeighing.peso_bruto || 0)} kg</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-zinc-500">Código Movimento</p>
                    <p className="text-sm font-mono text-white">{latestWeighing.codigo_movimento}</p>
                  </div>
                  <div>
                    <p className="text-xs text-zinc-500">Status</p>
                    <Badge className={latestWeighing.integrado ? "bg-green-500/20 text-green-400" : "bg-yellow-500/20 text-yellow-400"}>
                      {latestWeighing.integrado ? "Integrado" : "Pendente"}
                    </Badge>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-zinc-500">Data/Hora</p>
                  <p className="text-sm font-mono text-zinc-400">{formatDate(latestWeighing.data_movimento)}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-zinc-500">
                <Scale className="w-12 h-12 mb-2 opacity-50" />
                <p className="text-sm">Nenhuma pesagem registrada</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="recent-logs-card">
        <CardHeader>
          <CardTitle className="text-lg font-mono text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            Atividade Recente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentLogs.length > 0 ? (
            <div className="space-y-2">
              {recentLogs.map((log) => (
                <div 
                  key={log.id} 
                  className="flex items-start gap-3 p-2 rounded-sm bg-zinc-800/30 hover:bg-zinc-800/50 transition-colors"
                >
                  <span className={`mt-1 w-2 h-2 rounded-full ${
                    log.level === 'error' ? 'bg-red-400' :
                    log.level === 'warning' ? 'bg-yellow-400' :
                    log.level === 'success' ? 'bg-green-400' : 'bg-blue-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{log.message}</p>
                    <p className="text-xs text-zinc-500 font-mono">{formatDate(log.timestamp)}</p>
                  </div>
                  {log.flow_name && (
                    <Badge variant="outline" className="text-xs shrink-0">{log.flow_name}</Badge>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-zinc-500 text-center py-4">Nenhuma atividade recente</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
